import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ChatAlt2Icon,
  FilterIcon,
  SearchIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ArrowLeftIcon,
  EyeIcon
} from '@heroicons/react/outline';

const MyResponses = () => {
  const { getResponsesToUserRFQs } = useAuth();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getResponsesToUserRFQs();
      if (result.success) {
        setResponses(result.responses);
      }
    } catch (error) {
      console.error('Error loading responses:', error);
    }
    setLoading(false);
  }, [getResponsesToUserRFQs]);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  const filteredResponses = responses.filter(response => {
    const matchesSearch = response.originalPostTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         response.authorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         response.textContent?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || response.originalPostCategory === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter
  const categories = ['all', ...new Set(responses.map(r => r.originalPostCategory).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-CC5500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link
                to="/supplier-dashboard"
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Responses to My RFQs
                </h1>
                <p className="text-gray-600">
                  View and manage responses from suppliers
                </p>
              </div>
            </div>
            <Link
              to="/my-rfqs"
              className="inline-flex items-center px-4 py-2 bg-CC5500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <ChatAlt2Icon className="h-4 w-4 mr-2" />
              View My RFQs
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search responses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-CC5500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="md:w-48">
              <div className="relative">
                <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-CC5500 focus:border-transparent"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Responses List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredResponses.length} Response{filteredResponses.length !== 1 ? 's' : ''} found
            </h2>
          </div>

          <div className="p-6">
            {filteredResponses.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <ChatAlt2Icon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {responses.length === 0 ? 'No responses yet' : 'No responses match your search'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {responses.length === 0 
                    ? 'When suppliers respond to your RFQs, they\'ll appear here'
                    : 'Try adjusting your search or filter criteria'
                  }
                </p>
                {responses.length === 0 && (
                  <Link
                    to="/create-rfq"
                    className="inline-flex items-center px-4 py-2 bg-CC5500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Create Your First RFQ
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredResponses.map((response) => (
                  <div key={response.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Response to: {response.originalPostTitle}
                          </h3>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <ChatAlt2Icon className="h-3 w-3 mr-1" />
                            New Response
                          </span>
                        </div>
                        
                        <div className="flex items-center mb-3 text-sm text-gray-500">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          <span className="font-medium">{response.authorName || 'Anonymous'}</span>
                          <span className="mx-2">•</span>
                          <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                          <span>Budget: {response.originalPostBudget || 'Not specified'}</span>
                          <span className="mx-2">•</span>
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <span>{new Date(response.timestamp).toLocaleDateString()}</span>
                        </div>

                        {response.textContent && (
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <p className="text-gray-700 leading-relaxed">
                              {response.textContent}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                              {response.originalPostCategory}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-green-600 transition-colors">
                              <ChatAlt2Icon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyResponses; 